import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Loader2, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { SectionHeading } from "@/components/sections";
import { Reveal } from "@/components/motion";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { SITE } from "@/lib/site";
import { useAppointment } from "@/components/AppointmentContext";

const EMPTY = { name: "", email: "", phone: "", subject: "General Inquiry", message: "" };

export default function Contact() {
  const { openDialog } = useAppointment();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const crumbs = [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent — we'll reply within one business day.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please call us at " + SITE.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Contact Us" path="/contact"
        description="Contact Beacon Counseling in Southington, CT. Call 860-800-4758 or send a message to schedule evidence-based CBT therapy. Telehealth available across Connecticut."
        jsonLd={[orgSchema, breadcrumbSchema(crumbs)]} />

      {/* Hero + form */}
      <section className="relative overflow-hidden bg-navy pt-32 pb-24 lg:pt-40">
        <div className="pointer-events-none absolute -top-32 -right-20 h-96 w-96 rounded-full bg-teal/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-softblue-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
            {/* Left: info */}
            <div>
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">Get in Touch</span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white text-balance">
                  Let's take the first step, together.
                </h1>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-6 max-w-lg text-lg text-white/65 leading-relaxed">
                  Have a question, or ready to begin? Reach out however feels easiest — we respond to every message within one business day.
                </p>
              </Reveal>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                <ContactCard icon={Phone} label="Call us" value={SITE.phone} href={SITE.phoneHref} />
                <ContactCard icon={Mail} label="Email us" value={SITE.email} href={SITE.emailHref} />
                <ContactCard icon={MapPin} label="Visit us" value={SITE.address} />
                <div className="rounded-2xl glass-navy p-5">
                  <div className="flex items-center gap-2 text-teal-light"><Clock className="h-5 w-5" /><span className="text-sm font-medium">Office Hours</span></div>
                  <ul className="mt-3 space-y-1.5 text-sm text-white/70">
                    {SITE.hours.map((h) => (
                      <li key={h.day} className="flex justify-between gap-4"><span>{h.day}</span><span className="text-white/90">{h.time}</span></li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                <p className="text-sm text-amber-100/90">{SITE.crisis}</p>
              </div>
            </div>

            {/* Right: form */}
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] bg-white p-7 shadow-premium sm:p-9" data-testid="contact-form">
                {sent ? (
                  <div className="py-10 text-center">
                    <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-teal/10 text-teal"><Check className="h-8 w-8" /></div>
                    <h3 className="font-display text-3xl text-navy">Message sent.</h3>
                    <p className="mt-3 text-muted-foreground">Thank you for reaching out. We'll be in touch within one business day.</p>
                    <Button onClick={() => setSent(false)} className="mt-6 rounded-full bg-teal hover:bg-teal-dark px-8">Send another</Button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="grid gap-5">
                    <h2 className="font-display text-2xl text-navy">Send us a message</h2>
                    <div className="grid gap-1.5">
                      <Label className="text-sm text-navy/80">Full name <span className="text-teal">*</span></Label>
                      <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="h-12 rounded-xl" />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="grid gap-1.5">
                        <Label className="text-sm text-navy/80">Email <span className="text-teal">*</span></Label>
                        <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className="h-12 rounded-xl" />
                      </div>
                      <div className="grid gap-1.5">
                        <Label className="text-sm text-navy/80">Phone</Label>
                        <Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="(860) 555-0123" className="h-12 rounded-xl" />
                      </div>
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-sm text-navy/80">Subject</Label>
                      <Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} className="h-12 rounded-xl" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-sm text-navy/80">Message <span className="text-teal">*</span></Label>
                      <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us a little about what you're looking for…" className="rounded-xl resize-none" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="contact-submit"
                      className="h-13 py-6 w-full rounded-full bg-teal hover:bg-teal-dark text-white text-base">
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send Message"}
                    </Button>
                    <button type="button" onClick={openDialog} className="text-center text-sm text-muted-foreground hover:text-teal transition-colors">
                      …or request an appointment directly →
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Find Us" title="209 Main Street, Southington, CT" />
          <Reveal delay={0.1}>
            <div className="mt-12 overflow-hidden rounded-[2rem] border border-navy-100 shadow-premium">
              <iframe
                title="Beacon Counseling location map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.mapQuery)}&output=embed`}
                className="h-[420px] w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-embed" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="rounded-2xl glass-navy p-5 transition-colors hover:bg-white/10">
      <div className="flex items-center gap-2 text-teal-light"><Icon className="h-5 w-5" /><span className="text-sm font-medium">{label}</span></div>
      <div className="mt-2 text-white/90 break-words">{value}</div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : inner;
}
